import { useEffect, useState } from 'react';

export const SHELL_COMPACT_NAV_BREAKPOINT = '(max-width: 1024px)';
export const SHELL_PHONE_BREAKPOINT = '(max-width: 767px)';
export const SHELL_TOUCH_PRIMARY_MEDIA = '(hover: none) and (pointer: coarse)';

/** @deprecated Use SHELL_COMPACT_NAV_BREAKPOINT. Kept for backward compatibility. */
export const SHELL_NARROW_BREAKPOINT = SHELL_COMPACT_NAV_BREAKPOINT;

export interface ShellLayoutState {
  /** Drawer/slide-out nav (compact width or touch-primary input). */
  useCompactNav: boolean;
  /** Touch-primary device — used for selection/tap CSS hooks. */
  isTouchPrimary: boolean;
  /** Alias of useCompactNav for existing consumers. */
  isNarrow: boolean;
  /** Phone portrait/small width — dense toolbars and phone-only chrome gate here. */
  isPhone: boolean;
  /** Office document editing allowed above compact/tablet width (wider than 1024px). */
  canEditOfficeDocuments: boolean;
}

/**
 * Workspace split panes are half-width iframes. Office-edit and nav breakpoints
 * must follow the host desktop/phone viewport, not the pane.
 */
export function layoutMediaWindow(): Window {
  if (typeof window === 'undefined') {
    return undefined as unknown as Window;
  }
  try {
    const host = window.parent;
    if (host && host !== window && typeof host.matchMedia === 'function') {
      return host;
    }
  } catch {
    // Cross-origin iframe — fall back to this window.
  }
  return window;
}

export function readShellLayoutState(
  mediaWindow: Pick<Window, 'matchMedia'> | undefined = layoutMediaWindow()
): ShellLayoutState {
  if (!mediaWindow?.matchMedia) {
    return {
      useCompactNav: false,
      isTouchPrimary: false,
      isNarrow: false,
      isPhone: false,
      canEditOfficeDocuments: true
    };
  }

  const isCompactWidth = mediaWindow.matchMedia(SHELL_COMPACT_NAV_BREAKPOINT).matches;
  const isTouchPrimary = mediaWindow.matchMedia(SHELL_TOUCH_PRIMARY_MEDIA).matches;
  const isPhone = mediaWindow.matchMedia(SHELL_PHONE_BREAKPOINT).matches;
  const useCompactNav = isCompactWidth;

  return {
    useCompactNav,
    isTouchPrimary,
    isNarrow: useCompactNav,
    isPhone,
    canEditOfficeDocuments: !useCompactNav
  };
}

export function useShellLayout(): ShellLayoutState {
  const [layout, setLayout] = useState<ShellLayoutState>(() => readShellLayoutState());

  useEffect(() => {
    const mediaWindow = layoutMediaWindow();
    if (!mediaWindow?.matchMedia) return;

    const compactMq = mediaWindow.matchMedia(SHELL_COMPACT_NAV_BREAKPOINT);
    const phoneMq = mediaWindow.matchMedia(SHELL_PHONE_BREAKPOINT);
    const touchMq = mediaWindow.matchMedia(SHELL_TOUCH_PRIMARY_MEDIA);

    const sync = () => setLayout(readShellLayoutState());

    sync();
    compactMq.addEventListener('change', sync);
    phoneMq.addEventListener('change', sync);
    touchMq.addEventListener('change', sync);
    return () => {
      compactMq.removeEventListener('change', sync);
      phoneMq.removeEventListener('change', sync);
      touchMq.removeEventListener('change', sync);
    };
  }, []);

  return layout;
}
